import {
    ROOM_CREATED,
    ROOM_STATE,
    ROOM_ERROR,
    ROOM_LEAVE,
    ROOM_JOIN,
    ROOM_START_GAME,
    ROOM_RECONNECT,
    ROOM_RECONNECTING,
    ROOM_MIRROR_JOIN,
    ADD_PLAYER,
    REMOVE_PLAYER,
    UPDATE_PLAYER,
    ADD_AVATAR,
    UPDATE_AVATAR,
} from "actions/types";
import * as Colyseus from "@colyseus/sdk";
import { findPlayableGameById } from "constants/GameListAdapter";
import Store from "store/reduxstore";
import { UAParser } from "ua-parser-js";
import * as Sentry from "@sentry/react";
import { APP_TYPES } from "../constants/AppTypes";
import { sessionEnded } from "actions/auth";
//import { datadogRum } from '@datadog/browser-rum';

console.log(process.env.REACT_APP_GAME_SERVER_URL);
const client = new Colyseus.Client(process.env.REACT_APP_GAME_SERVER_URL);

// Debounce for redirect_to_external_game messages to prevent duplicates
let lastExternalRedirectTimestamp = 0;
const EXTERNAL_REDIRECT_DEBOUNCE_MS = 5000;

// Disable SDK auto-reconnect on page navigation/refresh.
// The 0.17 SDK auto-reconnects on close codes 1001/1005/1006/4010, which
// creates ghost connections that interfere with manual reconnection from
// the new page. Setting maxRetries=0 on beforeunload prevents this.
let _beforeUnloadHandler = null;
function setupBeforeUnloadHandler(room) {
    if (_beforeUnloadHandler) {
        window.removeEventListener('beforeunload', _beforeUnloadHandler);
    }
    _beforeUnloadHandler = () => { room.reconnection.maxRetries = 0; };
    window.addEventListener('beforeunload', _beforeUnloadHandler);
}

// Server's allowReconnection() window is typically 30s. We give up after that and
// fall back to ROOM_LEAVE (which sends the user to /join).
const RECONNECT_TIMEOUT_MS = 30000;

// Dedupe in-flight reconnection attempts so multiple onLeave fires (e.g. SDK retry
// and our handler both observing the same close) don't double-trigger.
const _reconnectingRooms = new Set();

// Handle any non-permanent WS close: show the "Reconnecting…" overlay and explicitly
// drive client.reconnect() ourselves. SDK auto-retry on the dying room is disabled
// so we don't end up with ghost connections racing our explicit call. Code 4200-4299
// (session ended) is handled separately by the caller before this is invoked.
function handleTransientLeave(dispatch, dyingRoom, code) {
    const state = Store.getState().room;
    // Race guard: leaveRoom() dispatches ROOM_LEAVE before onLeave fires, so a
    // consented leave (room.leave(true) → close code 1000) lands here with
    // roomOpen already false. Don't flip the UI back to reconnecting.
    if (!state.roomOpen) return;

    const reconnectionToken = dyingRoom?.reconnectionToken;
    const roomId = dyingRoom?.id;

    if (!reconnectionToken) {
        dispatch({ type: ROOM_LEAVE, payload: { errorCode: code } });
        return;
    }
    if (_reconnectingRooms.has(roomId)) {
        console.log(`[room] Reconnect already in flight for ${roomId}, skipping`);
        return;
    }
    _reconnectingRooms.add(roomId);

    // Kill the SDK's transport-level auto-retry on this dying room so it can't race
    // our explicit reconnect and produce a ghost connection.
    try { dyingRoom.reconnection.maxRetries = 0; } catch (e) { /* ignore */ }

    console.log(`[room] Transient close (code ${code}) — explicit reconnect`);
    dispatch({ type: ROOM_RECONNECTING, payload: { code } });

    let settled = false;
    const finish = () => {
        settled = true;
        _reconnectingRooms.delete(roomId);
    };

    const timeoutId = setTimeout(() => {
        if (settled) return;
        finish();
        console.warn('[room] Reconnect timed out after 30s');
        dispatch({ type: ROOM_LEAVE, payload: { errorCode: code } });
    }, RECONNECT_TIMEOUT_MS);

    // reconnectToRoom dispatches ROOM_RECONNECT on success (clears reconnecting,
    // restores room) or ROOM_ERROR on failure (clears reconnecting, sets roomOpen
    // false → Play.js navigates to /join). We just cancel the timeout.
    Promise.resolve(dispatch(reconnectToRoom(reconnectionToken)))
        .then(() => {
            if (settled) return;
            clearTimeout(timeoutId);
            finish();
        })
        .catch((err) => {
            if (settled) return;
            clearTimeout(timeoutId);
            finish();
            console.warn('[room] Reconnect threw:', err);
            dispatch({ type: ROOM_LEAVE, payload: { errorCode: code } });
        });
}

// Check if running on localhost
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Helper to get game URL from environment variable
// e.g., gameId "catchphrase" -> REACT_APP_CATCHPHRASE_URL
const getEnvGameUrl = (gameId) => {
    const envKey = `REACT_APP_${gameId.toUpperCase()}_URL`;
    return process.env[envKey];
};

// Helper to normalize URL (trim whitespace and ensure it has a protocol)
const normalizeUrl = (url) => {
    if (!url) return url;
    // Trim whitespace from start and end
    url = url.trim();
    // If URL doesn't start with http:// or https://, add https://
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`;
    }
    return url;
};

// Helper to get game URL
// - On localhost: use env vars for local development
// - Not on localhost: use CMS database first, fall back to env vars
const getGameUrl = (gameId) => {
    // On localhost, use env vars for local development
    if (isLocalhost) {
        const envUrl = getEnvGameUrl(gameId);
        if (envUrl) {
            return normalizeUrl(envUrl);
        }
    }

    // Not on localhost (or no env var): try CMS database first
    const game = findPlayableGameById(gameId);
    if (game?.gameUrl) {
        return normalizeUrl(game.gameUrl);
    }

    // Fall back to environment variable if CMS has no URL
    const envUrl = getEnvGameUrl(gameId);
    if (envUrl) {
        console.log(`[room] No CMS URL for ${gameId}, using env fallback: ${envUrl}`);
        return normalizeUrl(envUrl);
    }

    return null;
};


// Extract a claim from a JWT token payload without verification
const getJwtClaim = (token, claimName) => {
    try {
        if (!token) return null;
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const payload = JSON.parse(atob(parts[1]));
        return payload[claimName] || null;
    } catch {
        return null;
    }
};

export const createRoom = (token, userId, platform = APP_TYPES.WEB, isRoku = false, QRSessionID = null, roomCode = null, externalGameToken = null) => (dispatch) => {
    const reduxStore = Store.getState();

    const uniqueId = localStorage.getItem("uniqueId");
    var parser = new UAParser();
    const deviceDetails = parser.getResult();
    const countryCode = reduxStore.pricing.countryCode;

    var user = localStorage.getItem("user");
    if (typeof user === "string") {
        user = JSON.parse(user);
    }

    console.log("Create Room user ", user);
    let password = null;
    console.log("user email : ", user.email);
    console.log("Room pin : ", user.roomPin);
    if (user?.roomPin) {
        console.log("Room pin : ", user.roomPin);
        password = user.roomPin;
    }

    // Extract session ID from JWT for game server session validation
    const sessionId = getJwtClaim(user?.token, 'bpg-sid');

    const networkDetails = {
        type: navigator?.connection?.type,
        effectiveType: navigator?.connection?.effectiveType,
        downlink: navigator?.connection?.downlink,
    };
    console.log("Redux action create room session: " + QRSessionID)
    return client.create("game_city_room", { isHost: true,
        userId: userId,
        email: reduxStore.auth.user?.email,
        hostUniqueId: uniqueId,
        deviceDetails,
        networkDetails,
        isRoku,
        QRSessionID,
        platform,
        countryCode,
        password,
        roomCode,
        externalGameToken,
        sessionId,
    }).then(room => {
        console.log("room created");
        console.log("Reconnection Token : ", room.reconnectionToken);
        setupBeforeUnloadHandler(room);
        //datadogRum.setUser({
        //    id: room.roomId,
        //    email: reduxStore.auth.user.email,
        //});

        const $ = Colyseus.getStateCallbacks(room);

        room.send("update_host_token", { reconnectionToken: room.reconnectionToken });
        room.onStateChange.once((state) => {
            console.log("this is the first room state!", state);
            //room.send("host_joined_lobby");
            console.log("room : ", room)
            dispatch({ type: ROOM_CREATED, payload: { room, state } });
        });
        room.onStateChange((state) => {
            console.log(room.name, "has new state:", state);

            dispatch({ type: ROOM_STATE, payload: { roomState: state, } });
        });


        $(room.state).avatarList.onAdd((avatar, key) => {
            //console.log(avatar, "has been added at", key);
            dispatch({ type: ADD_AVATAR, payload: { avatar, } });

            $(avatar).onChange(() => {
                //dispatch({ type: UPDATE_PLAYER, payload: { player, changes, } });
            });
            $(avatar).listen("id", (value) => {
                dispatch({ type: UPDATE_AVATAR, payload: { avatar, field: "id", value, } });
            });
            $(avatar).listen("inUse", (value) => {
                dispatch({ type: UPDATE_AVATAR, payload: { avatar, field: "inUse", value, } });
            });
            $(avatar).listen("isActive", (value) => {
                dispatch({ type: UPDATE_AVATAR, payload: { avatar, field: "isActive", value, } });
            });
        });

        $(room.state).players.onAdd((player, key) => {
            console.log(player, "has been added at", key);
            dispatch({ type: ADD_PLAYER, payload: { player, } });

            $(player).onChange(() => {
                //dispatch({ type: UPDATE_PLAYER, payload: { player, changes, } });
            });
            $(player).listen("avatar", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "avatar", value, } });
            });
            $(player).listen("name", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "name", value, } });
            });
            $(player).listen("gameVote", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "gameVote", value, } });
            });
            $(player).listen("connected", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "connected", value, } });
            });
            $(player).listen("connectingTimer", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "connectingTimer", value, } });
            });
            $(player).listen("primaryPlayer", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "primaryPlayer", value, } });
            });
        });

        $(room.state).players.onRemove((player, key) => {
            console.log(player, "has been removed at", key);
            
            dispatch({ type: REMOVE_PLAYER, payload: { player, } });
        });

        room.onMessage("game_starting", (message) => {
            console.log("game_starting", "received on", room.name, message);
            let gameURL = getGameUrl(message.gameId);
            dispatch({ type: ROOM_START_GAME, payload: { gameURL, gameId: message.gameId, reconnectionToken: room.reconnectionToken } });
        });

        // Handle redirect to external game server (e.g., SongBlast)
        // lobbyUrl and platform are stored in Redis, fetched by external game server
        // hostToken is passed via URL for host authentication
        room.onMessage("redirect_to_external_game", (message) => {
            console.log("redirect_to_external_game", "received on", room.name, message);

            // Debounce to prevent duplicate redirects from reconnection or message replay
            const now = Date.now();
            if (now - lastExternalRedirectTimestamp < EXTERNAL_REDIRECT_DEBOUNCE_MS) {
                console.log("Ignoring duplicate redirect_to_external_game (debounced)");
                return;
            }
            lastExternalRedirectTimestamp = now;

            // Get frontend URL from game data (CMS or env fallback)
            const frontendUrl = getGameUrl(message.gameId);
            const { roomCode, serverUrl, hostToken } = message;

            // Host URL includes token for authentication
            // Client URL is minimal (no sensitive data)
            const hostUrl = `${frontendUrl}?code=${roomCode}&token=${hostToken}`;
            const clientUrl = `${frontendUrl}/client?code=${roomCode}`;

            // This is createRoom (host), so use hostUrl
            dispatch({ type: ROOM_START_GAME, payload: {
                gameURL: hostUrl,
                gameId: message.gameId,
                reconnectionToken: room.reconnectionToken,
                isExternalGame: true,
                roomCode,
                hostUrl,
                clientUrl,
                serverUrl,  // Colyseus server for API calls
            }});
        });

        room.onError((code, message) => {
            const currentRoom = Store.getState().room.room;
            if (currentRoom && currentRoom !== room) {
                console.warn(`[room] Ignoring onError from stale room ${room.id} (current: ${currentRoom.id}), code: ${code}`);
                return;
            }
            console.log(code, " error ", message);

            dispatch({ type: ROOM_ERROR, payload: { error: message, errorMessage: message, errorCode: code } });
        });

        room.onLeave((code) => {
            const currentRoom = Store.getState().room.room;
            if (currentRoom && currentRoom !== room) {
                console.warn(`[room] Ignoring onLeave from stale room ${room.id} (current: ${currentRoom.id}), code: ${code}`);
                return;
            }
            console.log(client.id, "left", room.name, "code", code);

            if (code >= 4200 && code < 4300) {
                dispatch(sessionEnded("Your game was ended because your session expired or was ended on another device."));
                return;
            }

            // Any non-session-ended close: try to recover the seat via explicit
            // reconnect. handleTransientLeave handles the consented-leave race
            // (where leaveRoom() already dispatched ROOM_LEAVE and roomOpen is
            // already false — it short-circuits in that case).
            handleTransientLeave(dispatch, room, code);
        });

        return { Status: "Success", room };

    }).catch((err, errorMessage) => {
        console.log("CREATE ERROR", err);
        dispatch({ type: ROOM_ERROR, payload: { error: err, message: err.message } });
        Sentry.captureMessage("Error Creating Room : " + JSON.stringify(err));
        return { Status: "Error", Message: "There was a problem creating your room." };
    });

};


export const joinRoom = (roomId, options) => (dispatch) => {

    const uniqueId = localStorage.getItem("uniqueId");
    options.uniqueId = uniqueId;

    var parser = new UAParser();
    options.deviceDetails = parser.getResult();

    const networkDetails = {
        type: navigator?.connection?.type,
        effectiveType: navigator?.connection?.effectiveType,
        downlink: navigator?.connection?.downlink,
    };
    options.networkDetails = networkDetails;

    return client.joinById(roomId, options).then(room => {
        console.log(room.sessionId, "joined", room.name);
        setupBeforeUnloadHandler(room);
        //this.storeSessionId(room.sessionId);
        //this.storeRoomId(roomId);
        console.log("Reconnection Token : ", room.reconnectionToken);

        const $ = Colyseus.getStateCallbacks(room);

        // Don't send update_player_token on initial join - OnJoinCommand.js already sets the
        // full token (ROOMCODE:sessionId-V3) server-side. Sending this would overwrite with
        // the client's token which may not have the room code prefix yet.
        // room.send("update_player_token", { reconnectionToken: room.reconnectionToken });
        room.onStateChange.once((state) => {
            console.log("this is the first room state!", JSON.stringify(state));

            dispatch({ type: ROOM_JOIN, payload: { room, state } });
            //this.startLocationChecks();
        });

        room.onStateChange((state) => {
            console.log(room.name, "has new state:", state);

            dispatch({ type: ROOM_STATE, payload: { roomState: state, } });
        });

        $(room.state).avatarList.onAdd((avatar, key) => {
            console.log(avatar, "has been added at", key);
            dispatch({ type: ADD_AVATAR, payload: { avatar, } });

            $(avatar).onChange(() => {
                //dispatch({ type: UPDATE_PLAYER, payload: { player, changes, } });
            });
            $(avatar).listen("id", (value) => {
                dispatch({ type: UPDATE_AVATAR, payload: { avatar, field: "id", value, } });
            });
            $(avatar).listen("inUse", (value) => {
                dispatch({ type: UPDATE_AVATAR, payload: { avatar, field: "inUse", value, } });
            });
            $(avatar).listen("isActive", (value) => {
                dispatch({ type: UPDATE_AVATAR, payload: { avatar, field: "isActive", value, } });
            });
        });

        $(room.state).players.onAdd((player, key) => {
            console.log(player, "has been added at", key);
            dispatch({ type: ADD_PLAYER, payload: { player, } });

            $(player).onChange(() => {
                //dispatch({ type: UPDATE_PLAYER, payload: { player, changes, } });
            });
            $(player).listen("avatar", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "avatar", value, } });
            });
            $(player).listen("name", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "name", value, } });
            });
            $(player).listen("gameVote", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "gameVote", value, } });
            });
            $(player).listen("connected", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "connected", value, } });
            });
            $(player).listen("connectingTimer", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "connectingTimer", value, } });
            });
            $(player).listen("primaryPlayer", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "primaryPlayer", value, } });
            });
        });

        $(room.state).players.onRemove((player, key) => {
            console.log(player, "has been removed at", key);

            dispatch({ type: REMOVE_PLAYER, payload: { player, } });
        });

        room.onMessage("game_starting", (message) => {
            console.log("game_starting", "received on", room.name, message);
            let gameURL = getGameUrl(message.gameId);
            dispatch({ type: ROOM_START_GAME, payload: { gameURL, gameId: message.gameId, reconnectionToken: room.reconnectionToken } });
        });

        // Handle redirect to external game server (e.g., SongBlast)
        // lobbyUrl and platform are stored in Redis, fetched by external game server
        // hostToken is passed via URL for host authentication
        room.onMessage("redirect_to_external_game", (message) => {
            console.log("redirect_to_external_game", "received on", room.name, message);

            // Debounce to prevent duplicate redirects from reconnection or message replay
            const now = Date.now();
            if (now - lastExternalRedirectTimestamp < EXTERNAL_REDIRECT_DEBOUNCE_MS) {
                console.log("Ignoring duplicate redirect_to_external_game (debounced)");
                return;
            }
            lastExternalRedirectTimestamp = now;

            // Get frontend URL from game data (CMS or env fallback)
            const frontendUrl = getGameUrl(message.gameId);
            const { roomCode, serverUrl, hostToken } = message;

            // Get current player's name and avatar to pass to external game (for display)
            const myPlayer = room.state.players.get(room.sessionId);
            const playerName = myPlayer?.name || '';
            const playerAvatar = myPlayer?.avatar || '';

            // Host URL includes token for authentication
            // Client URL includes name/avatar for display (no sensitive data)
            const hostUrl = `${frontendUrl}?code=${roomCode}&token=${hostToken}`;
            let clientUrl = `${frontendUrl}/client?code=${roomCode}`;
            if (playerName) {
                clientUrl += `&name=${encodeURIComponent(playerName)}`;
            }
            if (playerAvatar) {
                clientUrl += `&avatar=${encodeURIComponent(playerAvatar)}`;
            }

            // This is joinRoom (player), so use clientUrl
            dispatch({ type: ROOM_START_GAME, payload: {
                gameURL: clientUrl,
                gameId: message.gameId,
                reconnectionToken: room.reconnectionToken,
                isExternalGame: true,
                roomCode,
                hostUrl,
                clientUrl,
                serverUrl,  // Colyseus server for API calls
            }});
        });

        room.onError((code, message) => {
            const currentRoom = Store.getState().room.room;
            if (currentRoom && currentRoom !== room) {
                console.warn(`[room] Ignoring onError from stale room ${room.id} (current: ${currentRoom.id}), code: ${code}`);

                return;
            }
            console.log(code, " error ", message);
            dispatch({ type: ROOM_ERROR, payload: { message, code } });
        });

        room.onLeave((code) => {
            const currentRoom = Store.getState().room.room;
            if (currentRoom && currentRoom !== room) {
                console.warn(`[room] Ignoring onLeave from stale room ${room.id} (current: ${currentRoom.id}), code: ${code}`);

                return;
            }
            console.log(client.id, "left", room.name, "code", code);

            if (code >= 4200 && code < 4300) {
                dispatch(sessionEnded("Your game was ended because your session expired or was ended on another device."));
                return;
            }

            // Any non-session-ended close: try to recover the seat via explicit
            // reconnect. handleTransientLeave handles the consented-leave race
            // (where leaveRoom() already dispatched ROOM_LEAVE and roomOpen is
            // already false — it short-circuits in that case).
            handleTransientLeave(dispatch, room, code);
        });

        return { Status: "Success", room };
    }).catch((err, errorMessage) => {
        console.log("JOIN ERROR", err);

        const message = err.message ? err.message : "An error occured.";
        dispatch({ type: ROOM_ERROR, payload: { error: err, message, } });
        Sentry.captureMessage("Error Joining Room : " + JSON.stringify(err));

        return { Status: "Error", Message: message};
    });

};

export const reconnectToRoom = (token) => (dispatch) => {
    return client.reconnect(token).then(room => {
        console.log(room.sessionId, "joined", room.name);
        //this.storeSessionId(room.sessionId);
        //this.storeRoomId(roomId);
        console.log("Reconnection Token : ", room.reconnectionToken);
        setupBeforeUnloadHandler(room);

        const $ = Colyseus.getStateCallbacks(room);

        room.onStateChange.once((state) => {
            console.log("this is the first room state!", state);

            // Optional-chain state.host — on a cross-origin reconnect the first state
            // snapshot can arrive before the host schema ref has re-synced. Throwing here
            // would crash the callback and close the WS, tripping "reconnection denied"
            // on the server. If host is missing on this tick, skip the host/player split
            // and send nothing; the next onStateChange will carry the populated host and
            // the reducer will handle it via ROOM_STATE.
            const hostId = room.state.host?.id;
            if (hostId) {
                if (hostId === room.sessionId) {
                    room.send("update_host_token", { reconnectionToken: room.reconnectionToken });
                    room.send("host_joined_lobby", {});
                } else {
                    room.send("update_player_token", { reconnectionToken: room.reconnectionToken });
                }
            }

            dispatch({ type: ROOM_RECONNECT, payload: { room, state, } });
            //this.startLocationChecks();
        });

        room.onStateChange((state) => {
            console.log(room.name, "has new state:", state);

            dispatch({ type: ROOM_STATE, payload: { roomState: state, } });
        });

        $(room.state).avatarList.onAdd((avatar, key) => {
            //console.log(avatar, "has been added at", key);
            dispatch({ type: ADD_AVATAR, payload: { avatar, } });

            $(avatar).onChange(() => {
                //dispatch({ type: UPDATE_PLAYER, payload: { player, changes, } });
            });
            $(avatar).listen("id", (value) => {
                dispatch({ type: UPDATE_AVATAR, payload: { avatar, field: "id", value, } });
            });
            $(avatar).listen("inUse", (value) => {
                dispatch({ type: UPDATE_AVATAR, payload: { avatar, field: "inUse", value, } });
            });
            $(avatar).listen("isActive", (value) => {
                dispatch({ type: UPDATE_AVATAR, payload: { avatar, field: "isActive", value, } });
            });
        });

        $(room.state).players.onAdd((player, key) => {
            console.log(player, "has been added at", key);
            dispatch({ type: ADD_PLAYER, payload: { player, } });

            $(player).onChange(() => {
                //dispatch({ type: UPDATE_PLAYER, payload: { player, changes, } });
            });
            $(player).listen("avatar", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "avatar", value, } });
            });
            $(player).listen("name", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "name", value, } });
            });
            $(player).listen("gameVote", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "gameVote", value, } });
            });
            $(player).listen("connected", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "connected", value, } });
            }); 
            $(player).listen("connectingTimer", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "connectingTimer", value, } });
            });
            $(player).listen("primaryPlayer", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "primaryPlayer", value, } });
            });
        });

        $(room.state).players.onRemove((player, key) => {
            console.log(player, "has been removed at", key);

            dispatch({ type: REMOVE_PLAYER, payload: { player, } });
        });

        room.onMessage("game_starting", (message) => {
            console.log("game_starting", "received on", room.name, message);
            let gameURL = getGameUrl(message.gameId);
            dispatch({ type: ROOM_START_GAME, payload: { gameURL, gameId: message.gameId, reconnectionToken: room.reconnectionToken } });
        });

        // Handle redirect to external game server (e.g., SongBlast)
        // lobbyUrl and platform are stored in Redis, fetched by external game server
        // hostToken is passed via URL for host authentication
        room.onMessage("redirect_to_external_game", (message) => {
            console.log("redirect_to_external_game", "received on", room.name, message);

            // Debounce to prevent duplicate redirects from reconnection or message replay
            const now = Date.now();
            if (now - lastExternalRedirectTimestamp < EXTERNAL_REDIRECT_DEBOUNCE_MS) {
                console.log("Ignoring duplicate redirect_to_external_game (debounced)");
                return;
            }
            lastExternalRedirectTimestamp = now;

            // Get frontend URL from game data (CMS or env fallback)
            const frontendUrl = getGameUrl(message.gameId);
            const { roomCode, serverUrl, hostToken } = message;

            // Get current player's name and avatar to pass to external game (for display)
            const myPlayer = room.state.players.get(room.sessionId);
            const playerName = myPlayer?.name || '';
            const playerAvatar = myPlayer?.avatar || '';

            // Host URL includes token for authentication
            // Client URL includes name/avatar for display (no sensitive data)
            const hostUrl = `${frontendUrl}?code=${roomCode}&token=${hostToken}`;
            let clientUrl = `${frontendUrl}/client?code=${roomCode}`;
            if (playerName) {
                clientUrl += `&name=${encodeURIComponent(playerName)}`;
            }
            if (playerAvatar) {
                clientUrl += `&avatar=${encodeURIComponent(playerAvatar)}`;
            }

            // Check if this is the host or a player
            const isHost = room.state.host?.id === room.sessionId;
            dispatch({ type: ROOM_START_GAME, payload: {
                gameURL: isHost ? hostUrl : clientUrl,
                gameId: message.gameId,
                reconnectionToken: room.reconnectionToken,
                isExternalGame: true,
                roomCode,
                hostUrl,
                clientUrl,
                serverUrl,  // Colyseus server for API calls
            }});
        });

        room.onError((code, message) => {
            const currentRoom = Store.getState().room.room;
            if (currentRoom && currentRoom !== room) {
                console.warn(`[room] Ignoring onError from stale room ${room.id} (current: ${currentRoom.id}), code: ${code}`);

                return;
            }
            console.log(code, " error ", message);
            dispatch({ type: ROOM_ERROR, payload: { message, code } });
        });

        room.onLeave((code) => {
            const currentRoom = Store.getState().room.room;
            if (currentRoom && currentRoom !== room) {
                console.warn(`[room] Ignoring onLeave from stale room ${room.id} (current: ${currentRoom.id}), code: ${code}`);

                return;
            }
            console.log(client.id, "left", room.name, "code", code);

            if (code >= 4200 && code < 4300) {
                dispatch(sessionEnded("Your game was ended because your session expired or was ended on another device."));
                return;
            }

            // Any non-session-ended close: try to recover the seat via explicit
            // reconnect. handleTransientLeave handles the consented-leave race
            // (where leaveRoom() already dispatched ROOM_LEAVE and roomOpen is
            // already false — it short-circuits in that case).
            handleTransientLeave(dispatch, room, code);
        });

        return { Status: "Success", room, };
    }).catch((err, errorMessage) => {
        console.log("JOIN ERROR", err);

        const message = err.message ? err.message : "An error occured.";
        dispatch({ type: ROOM_ERROR, payload: { error: err, message} });

        Sentry.captureMessage("Error Reconnecting to Room : " + JSON.stringify(err));
        return { Status: "Error", Message: message, };
    });

};


export const joinAsMirror = (roomId, password = null) => (dispatch, getState) => {
    const uniqueId = localStorage.getItem("uniqueId");
    var parser = new UAParser();
    const deviceDetails = parser.getResult();
    const reduxState = getState();
    const email = reduxState.auth?.user?.email || null;
    const platform = reduxState.devices?.appType || "web";

    const options = {
        isHost: true,
        isMirror: true,
        hostUniqueId: uniqueId,
        deviceDetails,
        email,
        platform,
    };
    if (password) options.password = password;

    return client.joinById(roomId, options).then(room => {
        setupBeforeUnloadHandler(room);
        console.log("Mirror joined room", room.sessionId, room.name);

        const $ = Colyseus.getStateCallbacks(room);

        room.onStateChange.once((state) => {
            console.log("Mirror: first room state!", state);
            dispatch({ type: ROOM_MIRROR_JOIN, payload: { room, state } });
        });

        room.onStateChange((state) => {
            dispatch({ type: ROOM_STATE, payload: { roomState: state } });
        });

        $(room.state).avatarList.onAdd((avatar, key) => {
            dispatch({ type: ADD_AVATAR, payload: { avatar } });
            $(avatar).listen("id", (value) => {
                dispatch({ type: UPDATE_AVATAR, payload: { avatar, field: "id", value } });
            });
            $(avatar).listen("inUse", (value) => {
                dispatch({ type: UPDATE_AVATAR, payload: { avatar, field: "inUse", value } });
            });
            $(avatar).listen("isActive", (value) => {
                dispatch({ type: UPDATE_AVATAR, payload: { avatar, field: "isActive", value } });
            });
        });

        $(room.state).players.onAdd((player, key) => {
            dispatch({ type: ADD_PLAYER, payload: { player } });
            $(player).listen("avatar", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "avatar", value } });
            });
            $(player).listen("name", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "name", value } });
            });
            $(player).listen("gameVote", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "gameVote", value } });
            });
            $(player).listen("connected", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "connected", value } });
            });
            $(player).listen("connectingTimer", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "connectingTimer", value } });
            });
            $(player).listen("primaryPlayer", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "primaryPlayer", value } });
            });
        });

        $(room.state).players.onRemove((player, key) => {
            dispatch({ type: REMOVE_PLAYER, payload: { player } });
        });

        room.onMessage("game_starting", (message) => {
            console.log("Mirror: game_starting", message);
            let gameURL = getGameUrl(message.gameId);
            dispatch({ type: ROOM_START_GAME, payload: { gameURL, gameId: message.gameId, reconnectionToken: room.reconnectionToken } });
        });

        room.onMessage("redirect_to_external_game", (message) => {
            console.log("Mirror: redirect_to_external_game", message);

            const now = Date.now();
            if (now - lastExternalRedirectTimestamp < EXTERNAL_REDIRECT_DEBOUNCE_MS) {
                console.log("Mirror: Ignoring duplicate redirect_to_external_game (debounced)");
                return;
            }
            lastExternalRedirectTimestamp = now;

            const frontendUrl = getGameUrl(message.gameId);
            const { roomCode, serverUrl, hostToken } = message;
            const hostUrl = `${frontendUrl}?code=${roomCode}&token=${hostToken}&mirror=true`;
            const clientUrl = `${frontendUrl}/client?code=${roomCode}`;

            dispatch({ type: ROOM_START_GAME, payload: {
                gameURL: hostUrl,
                gameId: message.gameId,
                reconnectionToken: room.reconnectionToken,
                isExternalGame: true,
                roomCode,
                hostUrl,
                clientUrl,
                serverUrl,
            }});
        });

        room.onError((code, message) => {
            const currentRoom = Store.getState().room.room;
            if (currentRoom && currentRoom !== room) return;
            console.log("Mirror error:", code, message);
            dispatch({ type: ROOM_ERROR, payload: { error: message, errorMessage: message, errorCode: code } });
        });

        room.onLeave((code) => {
            const currentRoom = Store.getState().room.room;
            if (currentRoom && currentRoom !== room) return;
            console.log("Mirror left room, code:", code);

            if (code >= 4200 && code < 4300) {
                dispatch(sessionEnded("Your game was ended because your session expired or was ended on another device."));
                return;
            }

            // Any non-session-ended close: try to recover the seat via explicit
            // reconnect. handleTransientLeave handles the consented-leave race
            // (where leaveRoom() already dispatched ROOM_LEAVE and roomOpen is
            // already false — it short-circuits in that case).
            handleTransientLeave(dispatch, room, code);
        });

        return { Status: "Success", room };
    }).catch((err) => {
        console.log("Mirror JOIN ERROR", err);
        dispatch({ type: ROOM_ERROR, payload: { error: err, message: err.message } });
        return { Status: "Error", Message: err.message || "Failed to join as mirror." };
    });
};

export const reconnectAsMirror = (token) => (dispatch) => {
    return client.reconnect(token).then(room => {
        console.log("Mirror reconnected", room.sessionId, room.name);
        setupBeforeUnloadHandler(room);

        const $ = Colyseus.getStateCallbacks(room);

        room.onStateChange.once((state) => {
            console.log("Mirror reconnect: first room state!", state);
            dispatch({ type: ROOM_MIRROR_JOIN, payload: { room, state } });
        });

        room.onStateChange((state) => {
            dispatch({ type: ROOM_STATE, payload: { roomState: state } });
        });

        $(room.state).avatarList.onAdd((avatar, key) => {
            dispatch({ type: ADD_AVATAR, payload: { avatar } });
            $(avatar).listen("id", (value) => {
                dispatch({ type: UPDATE_AVATAR, payload: { avatar, field: "id", value } });
            });
            $(avatar).listen("inUse", (value) => {
                dispatch({ type: UPDATE_AVATAR, payload: { avatar, field: "inUse", value } });
            });
            $(avatar).listen("isActive", (value) => {
                dispatch({ type: UPDATE_AVATAR, payload: { avatar, field: "isActive", value } });
            });
        });

        $(room.state).players.onAdd((player, key) => {
            dispatch({ type: ADD_PLAYER, payload: { player } });
            $(player).listen("avatar", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "avatar", value } });
            });
            $(player).listen("name", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "name", value } });
            });
            $(player).listen("gameVote", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "gameVote", value } });
            });
            $(player).listen("connected", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "connected", value } });
            });
            $(player).listen("connectingTimer", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "connectingTimer", value } });
            });
            $(player).listen("primaryPlayer", (value) => {
                dispatch({ type: UPDATE_PLAYER, payload: { player, field: "primaryPlayer", value } });
            });
        });

        $(room.state).players.onRemove((player, key) => {
            dispatch({ type: REMOVE_PLAYER, payload: { player } });
        });

        room.onMessage("game_starting", (message) => {
            console.log("Mirror: game_starting", message);
            let gameURL = getGameUrl(message.gameId);
            dispatch({ type: ROOM_START_GAME, payload: { gameURL, gameId: message.gameId, reconnectionToken: room.reconnectionToken } });
        });

        room.onMessage("redirect_to_external_game", (message) => {
            console.log("Mirror: redirect_to_external_game", message);

            const now = Date.now();
            if (now - lastExternalRedirectTimestamp < EXTERNAL_REDIRECT_DEBOUNCE_MS) {
                console.log("Mirror: Ignoring duplicate redirect_to_external_game (debounced)");
                return;
            }
            lastExternalRedirectTimestamp = now;

            const frontendUrl = getGameUrl(message.gameId);
            const { roomCode, serverUrl, hostToken } = message;
            const hostUrl = `${frontendUrl}?code=${roomCode}&token=${hostToken}&mirror=true`;
            const clientUrl = `${frontendUrl}/client?code=${roomCode}`;

            dispatch({ type: ROOM_START_GAME, payload: {
                gameURL: hostUrl,
                gameId: message.gameId,
                reconnectionToken: room.reconnectionToken,
                isExternalGame: true,
                roomCode,
                hostUrl,
                clientUrl,
                serverUrl,
            }});
        });

        room.onError((code, message) => {
            const currentRoom = Store.getState().room.room;
            if (currentRoom && currentRoom !== room) return;
            console.log("Mirror reconnect error:", code, message);
            dispatch({ type: ROOM_ERROR, payload: { error: message, errorMessage: message, errorCode: code } });
        });

        room.onLeave((code) => {
            const currentRoom = Store.getState().room.room;
            if (currentRoom && currentRoom !== room) return;
            console.log("Mirror reconnect left room, code:", code);

            if (code >= 4200 && code < 4300) {
                dispatch(sessionEnded("Your game was ended because your session expired or was ended on another device."));
                return;
            }

            // Any non-session-ended close: try to recover the seat via explicit
            // reconnect. handleTransientLeave handles the consented-leave race
            // (where leaveRoom() already dispatched ROOM_LEAVE and roomOpen is
            // already false — it short-circuits in that case).
            handleTransientLeave(dispatch, room, code);
        });

        return { Status: "Success", room };
    }).catch((err) => {
        console.log("Mirror RECONNECT ERROR", err);
        return { Status: "Error", Message: err.message || "Failed to reconnect as mirror." };
    });
};

export const leaveRoom = () => (dispatch) => {
    const reduxStore = Store.getState();
    reduxStore.room.room.leave(true);

    dispatch({ type: ROOM_LEAVE, payload: { } });
};